/**
 * `<ErrorIllustration>` — illustrations for the most-common
 * ceremony failure codes. Per the design-system contract §4.4.
 *
 * Embedded in `<CeremonyError>` to replace text-only diagnostics
 * with a visual that names the situation. Each variant shows
 * something specific to the failure mode — a cancelled prompt
 * with an arrow at the Cancel button, a Bluetooth-off icon, a
 * private-window indicator, etc. The accompanying text remedy
 * (already in CeremonyError) stays unchanged.
 *
 * # Descriptions (for designers / AI tools)
 *
 * - `user-cancelled`: An OS-prompt shape (rounded rectangle)
 *   with a clear "Cancel" button highlighted by a red arrow
 *   pointing at it. Conveys "you (or something) hit Cancel,
 *   not a system error." Calm, not accusatory.
 *
 * - `platform-authenticator-missing`: A laptop / device with
 *   a soft circle-and-slash over the fingerprint glyph,
 *   indicating "no biometric configured." A small wrench
 *   motif suggests "fixable in Settings."
 *
 * - `hybrid-bluetooth-unavailable`: A phone and laptop with a
 *   broken / dimmed Bluetooth wave between them. Communicates
 *   "the two devices couldn't connect." Reassuring tone.
 *
 * - `no-credentials`: A small empty box (or a key with a
 *   question mark), indicating "no matching passkey on this
 *   device." Pairs with the existing recovery-route copy.
 *
 * - `private-mode`: A browser window with the private/incognito
 *   indicator (a small mask glyph) and a soft warning aura.
 *   Conveys "your browser is in a mode that blocks passkeys."
 *
 * Composition rules: 240 × 140 px viewBox, foreground line-art,
 * danger or warning accent only on the specific failure
 * element. The illustration should be legible at small sizes
 * (this appears in a ceremony-error card, not a hero slot).
 */

import type { ReactElement } from "react";

export type ErrorIllustrationCode =
  | "user-cancelled"
  | "platform-authenticator-missing"
  | "hybrid-bluetooth-unavailable"
  | "no-credentials"
  | "private-mode"
  // P1 set (first-cut illustrations, refinement-pass pending —
  // see classifier mapping in ceremony-error.tsx for which
  // classification codes each one covers).
  | "took-too-long"
  | "already-registered"
  | "wrong-key"
  | "browser-blocked"
  | "interrupted";

export interface ErrorIllustrationProps {
  code: ErrorIllustrationCode;
  className?: string;
  width?: number;
}

export function ErrorIllustration({
  code,
  className,
  width = 200,
}: ErrorIllustrationProps): ReactElement {
  const v = VARIANTS[code];
  return (
    <figure className={className} style={{ width, margin: 0 }} aria-label={v.alt}>
      <svg
        role="img"
        aria-labelledby={`err-${code}-title`}
        viewBox={v.viewBox}
        width="100%"
        height="auto"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title id={`err-${code}-title`}>{v.alt}</title>
        {v.render()}
      </svg>
    </figure>
  );
}

interface VariantDef {
  alt: string;
  viewBox: string;
  render: () => ReactElement;
}

const VARIANTS: Record<ErrorIllustrationCode, VariantDef> = {
  "user-cancelled": {
    alt:
      "An OS authentication prompt with the Cancel button highlighted, " +
      "indicating that the sign-in was dismissed before completing.",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {/* Prompt frame */}
        <rect x="40" y="20" width="160" height="100" rx="10" fill="hsl(var(--card))" stroke="hsl(var(--border))" />
        {/* Dimmed fingerprint icon (slightly more detail than before) */}
        <g transform="translate(106, 36)" stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1.5" strokeLinecap="round">
          <path d="M 4 10 Q 14 -2, 24 10" />
          <path d="M 7 14 Q 14 4, 21 14" />
          <path d="M 10 18 Q 14 10, 18 18" />
          <path d="M 14 14 L 14 26" />
        </g>
        {/* Cancel button — highlighted, slightly larger to read */}
        <rect
          x="74"
          y="82"
          width="50"
          height="22"
          rx="5"
          fill="hsl(var(--danger) / 0.08)"
          stroke="hsl(var(--danger))"
          strokeWidth="2"
        />
        <text
          x={99}
          y={97}
          fontSize="10"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontWeight="600"
          fill="hsl(var(--danger))"
          textAnchor="middle"
        >
          Cancel
        </text>
        {/* Sign in button — muted, slightly larger to balance */}
        <rect x="132" y="82" width="50" height="22" rx="5" fill="hsl(var(--muted) / 0.4)" stroke="hsl(var(--border))" />

        {/*
          Senior-designer refinement: the arrow was a straight
          diagonal line. Replaced with a curved Bezier arc
          that arcs in from the upper-left and lands with a
          proper arrowhead pointing INTO the Cancel button.
          Reads as gestural (drawn by hand) rather than
          computer-drawn.
        */}
        <path
          d="M 32 58 Q 50 56, 68 78"
          stroke="hsl(var(--danger))"
          strokeWidth="2"
          fill="none"
        />
        {/* Arrowhead — two short strokes at the curve's end */}
        <path
          d="M 64 72 L 68 78 L 64 80"
          stroke="hsl(var(--danger))"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    ),
  },
  "platform-authenticator-missing": {
    alt:
      "A laptop with a fingerprint icon crossed out, indicating that " +
      "no biometric or PIN is configured on this device.",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Laptop body */}
        <rect x="60" y="32" width="120" height="76" rx="5" fill="hsl(var(--card))" stroke="hsl(var(--foreground))" />
        <rect x="68" y="40" width="104" height="60" rx="2" fill="hsl(var(--background))" stroke="hsl(var(--border) / 0.5)" strokeWidth="0.5" />
        <path d="M 50 108 L 190 108 L 198 120 L 42 120 Z" fill="hsl(var(--muted) / 0.4)" />

        {/* Fingerprint inside screen */}
        <g transform="translate(108, 54)" stroke="hsl(var(--muted-foreground))" strokeWidth="1.5">
          <path d="M 4 12 Q 12 0, 20 12" />
          <path d="M 7 16 Q 12 8, 17 16" />
          <path d="M 12 12 L 12 26" />
        </g>

        {/* Crossed-out circle over the fingerprint */}
        <circle
          cx="120"
          cy="70"
          r="22"
          fill="none"
          stroke="hsl(var(--warning))"
          strokeWidth="2.5"
        />
        <line
          x1="104"
          y1="86"
          x2="136"
          y2="54"
          stroke="hsl(var(--warning))"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </g>
    ),
  },
  "hybrid-bluetooth-unavailable": {
    alt:
      "A computer and a phone with a dimmed Bluetooth wave between them, " +
      "indicating that the two devices can't pair to complete sign-in.",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Laptop */}
        <rect x="20" y="40" width="80" height="50" rx="4" fill="hsl(var(--card))" />
        <rect x="24" y="44" width="72" height="42" rx="2" fill="hsl(var(--background))" stroke="hsl(var(--border) / 0.5)" strokeWidth="0.5" />
        <path d="M 14 90 L 106 90 L 110 98 L 10 98 Z" fill="hsl(var(--muted) / 0.4)" />

        {/* Bluetooth wave — dashed and dim */}
        <g stroke="hsl(var(--muted-foreground) / 0.5)" strokeWidth="1.5" strokeDasharray="3 4">
          <path d="M 108 64 Q 124 50, 140 64" />
          <path d="M 112 70 Q 124 58, 136 70" />
        </g>
        {/* Strikethrough across the connection */}
        <line
          x1="106"
          y1="78"
          x2="142"
          y2="48"
          stroke="hsl(var(--warning))"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Phone */}
        <rect x="150" y="28" width="46" height="76" rx="8" fill="hsl(var(--card))" stroke="hsl(var(--foreground))" />
        <rect x="156" y="36" width="34" height="58" rx="2" fill="hsl(var(--background))" stroke="hsl(var(--border) / 0.5)" strokeWidth="0.5" />
        <rect x="167" y="36" width="12" height="3" rx="1.5" fill="hsl(var(--foreground))" stroke="none" />
        <circle cx="173" cy="100" r="2" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
      </g>
    ),
  },
  "no-credentials": {
    alt:
      "A passkey icon with a small question mark beside it, indicating " +
      "no matching passkey is available on this device.",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Key glyph */}
        <circle cx="84" cy="70" r="18" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
        <circle cx="84" cy="70" r="6" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
        <line x1="102" y1="70" x2="148" y2="70" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
        <line x1="134" y1="70" x2="134" y2="80" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
        <line x1="144" y1="70" x2="144" y2="82" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />

        {/* Question mark in a circle */}
        <circle cx="180" cy="56" r="14" fill="hsl(var(--warning) / 0.15)" stroke="hsl(var(--warning))" strokeWidth="2" />
        <text
          x={180}
          y={62}
          fontSize="16"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontWeight="700"
          fill="hsl(var(--warning))"
          textAnchor="middle"
        >
          ?
        </text>

        {/* Hint dashes under the key — suggesting "no usable" */}
        <line x1="64" y1="100" x2="158" y2="100" stroke="hsl(var(--muted-foreground) / 0.4)" strokeDasharray="4 4" />
      </g>
    ),
  },
  "private-mode": {
    alt:
      "A browser window with the universal 'hat-and-glasses' incognito " +
      "silhouette centered inside, indicating private / incognito mode " +
      "where passkey storage may be unavailable.",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/*
          Senior-designer refinement (2026-05-11): the original
          "infinity-mask" shape didn't read as anything
          specific. Replaced with the universally-recognised
          Chrome incognito silhouette — a fedora hat above a
          pair of round glasses. Every browser user has seen
          this glyph; recognition is instant.
        */}

        {/* Browser window frame */}
        <rect x="40" y="20" width="160" height="100" rx="6" fill="hsl(var(--card))" stroke="hsl(var(--foreground))" />
        <line x1="40" y1="36" x2="200" y2="36" stroke="hsl(var(--border))" />
        <circle cx="50" cy="28" r="2.5" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
        <circle cx="58" cy="28" r="2.5" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
        <circle cx="66" cy="28" r="2.5" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
        <rect x="78" y="24" width="100" height="8" rx="3" fill="hsl(var(--muted) / 0.5)" stroke="none" />

        {/*
          Incognito silhouette — fedora hat (crown + brim) and
          round glasses with a nose bridge and short earpieces.
          Centered inside the browser content area.
        */}
        <g transform="translate(120, 76)">
          {/* Hat crown */}
          <path
            d="M -18 -10 Q -16 -20, 0 -20 Q 16 -20, 18 -10 L 18 -6 L -18 -6 Z"
            fill="hsl(var(--warning))"
            stroke="hsl(var(--warning))"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          {/* Hat brim */}
          <ellipse cx="0" cy="-6" rx="24" ry="3.5" fill="hsl(var(--warning))" stroke="hsl(var(--warning))" strokeWidth="1.2" />

          {/* Glasses lenses */}
          <circle cx="-9" cy="8" r="6.5" fill="hsl(var(--background))" stroke="hsl(var(--warning))" strokeWidth="2" />
          <circle cx="9" cy="8" r="6.5" fill="hsl(var(--background))" stroke="hsl(var(--warning))" strokeWidth="2" />
          {/* Nose bridge */}
          <line x1="-2.5" y1="8" x2="2.5" y2="8" stroke="hsl(var(--warning))" strokeWidth="2" strokeLinecap="round" />
          {/* Earpieces */}
          <line x1="-15.5" y1="8" x2="-22" y2="6" stroke="hsl(var(--warning))" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="15.5" y1="8" x2="22" y2="6" stroke="hsl(var(--warning))" strokeWidth="1.5" strokeLinecap="round" />
        </g>

        <text
          x={120}
          y={108}
          fontSize="9"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontWeight="500"
          fill="hsl(var(--muted-foreground))"
          textAnchor="middle"
        >
          Private / Incognito window
        </text>
      </g>
    ),
  },
  // ────────────────────────────────────────────────────────
  // P1 set — first-cut illustrations, refinement pass pending.
  //
  // Each follows the system's discipline (token-only colors,
  // matched stroke weights, hand-drawn line-art feel, no
  // pixel-accurate platform mockups) but has NOT yet had the
  // senior-designer refinement pass that the P0 set received
  // on 2026-05-11. Treat as functional placeholders that
  // communicate the situation correctly; expect a future pass
  // to polish proportions, optical centering, and stroke
  // weights against the four-tier system.
  // ────────────────────────────────────────────────────────
  "took-too-long": {
    alt:
      "An hourglass with the sand fully drained to the bottom, conveying " +
      "'this ceremony exceeded its time window.'",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Hourglass outline — two triangles meeting at a waist */}
        <g transform="translate(120, 70)">
          {/* Top cap + bottom cap (horizontal lines) —
              accent stroke 2.0 per the four-tier system. */}
          <line x1="-28" y1="-40" x2="28" y2="-40" strokeWidth="2" />
          <line x1="-28" y1="40" x2="28" y2="40" strokeWidth="2" />
          {/* Hourglass walls — diagonal lines meeting at center */}
          <line x1="-28" y1="-40" x2="0" y2="0" />
          <line x1="28" y1="-40" x2="0" y2="0" />
          <line x1="0" y1="0" x2="-28" y2="40" />
          <line x1="0" y1="0" x2="28" y2="40" />
          {/* Top chamber — empty (sand has drained) */}
          {/* Bottom chamber — filled with sand (a small mound) */}
          <path
            d="M -24 40 Q -24 32, -16 28 Q 0 22, 16 28 Q 24 32, 24 40 Z"
            fill="hsl(var(--warning) / 0.4)"
            stroke="hsl(var(--warning))"
            strokeWidth="1.4"
          />
          {/* Drained ribbon — soft dashed line in the upper chamber */}
          <line
            x1="0"
            y1="-36"
            x2="0"
            y2="-4"
            stroke="hsl(var(--muted-foreground) / 0.45)"
            strokeDasharray="2 4"
            strokeWidth="1.4"
          />
        </g>
      </g>
    ),
  },
  "already-registered": {
    alt:
      "A passkey with a small green checkmark badge, indicating " +
      "'this device is already enrolled.'",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Key glyph — same family as no-credentials, but uses primary color */}
        <circle cx="84" cy="70" r="18" stroke="hsl(var(--primary))" strokeWidth="2" />
        <circle cx="84" cy="70" r="6" fill="hsl(var(--primary))" stroke="none" />
        <line x1="102" y1="70" x2="148" y2="70" stroke="hsl(var(--primary))" strokeWidth="2" />
        <line x1="134" y1="70" x2="134" y2="80" stroke="hsl(var(--primary))" strokeWidth="2" />
        <line x1="144" y1="70" x2="144" y2="82" stroke="hsl(var(--primary))" strokeWidth="2" />
        {/* Success checkmark badge — top-right of the key.
            Stroke widths match the system: badge ring 2.0,
            checkmark accent 2.0. */}
        <circle cx="166" cy="48" r="14" fill="hsl(var(--success) / 0.15)" stroke="hsl(var(--success))" strokeWidth="2" />
        <path
          d="M 160 48 L 164.5 52.5 L 172 44"
          stroke="hsl(var(--success))"
          strokeWidth="2"
          fill="none"
        />
      </g>
    ),
  },
  "wrong-key": {
    alt:
      "Two passkeys side by side with a small mismatch indicator between " +
      "them, indicating 'the key you used isn't the one this account expects.'",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Left key — the one the user offered (muted) */}
        <g transform="translate(56, 70)">
          <circle cx="0" cy="0" r="14" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
          <circle cx="0" cy="0" r="4.5" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
          <line x1="14" y1="0" x2="40" y2="0" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
          <line x1="28" y1="0" x2="28" y2="8" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
          <line x1="36" y1="0" x2="36" y2="6" stroke="hsl(var(--muted-foreground))" strokeWidth="2" />
        </g>
        {/* Mismatch glyph — a small X between the two keys */}
        <g transform="translate(120, 70)">
          <circle cx="0" cy="0" r="11" fill="hsl(var(--warning) / 0.15)" stroke="hsl(var(--warning))" strokeWidth="2" />
          <line x1="-4" y1="-4" x2="4" y2="4" stroke="hsl(var(--warning))" strokeWidth="2" />
          <line x1="-4" y1="4" x2="4" y2="-4" stroke="hsl(var(--warning))" strokeWidth="2" />
        </g>
        {/* Right key — the one the account expects (primary). Mirrored. */}
        <g transform="translate(184, 70) scale(-1, 1)">
          <circle cx="0" cy="0" r="14" stroke="hsl(var(--primary))" strokeWidth="2" />
          <circle cx="0" cy="0" r="4.5" fill="hsl(var(--primary))" stroke="none" />
          <line x1="14" y1="0" x2="40" y2="0" stroke="hsl(var(--primary))" strokeWidth="2" />
          <line x1="28" y1="0" x2="28" y2="8" stroke="hsl(var(--primary))" strokeWidth="2" />
          <line x1="36" y1="0" x2="36" y2="6" stroke="hsl(var(--primary))" strokeWidth="2" />
        </g>
      </g>
    ),
  },
  "browser-blocked": {
    alt:
      "A browser window with a 'blocked' shield over the content area, " +
      "indicating 'the browser refused this ceremony for security reasons.'",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Browser window frame */}
        <rect x="40" y="20" width="160" height="100" rx="6" fill="hsl(var(--card))" stroke="hsl(var(--foreground))" />
        <line x1="40" y1="36" x2="200" y2="36" stroke="hsl(var(--border))" />
        <circle cx="50" cy="28" r="2.5" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
        <circle cx="58" cy="28" r="2.5" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
        <circle cx="66" cy="28" r="2.5" fill="hsl(var(--muted-foreground) / 0.4)" stroke="none" />
        <rect x="78" y="24" width="100" height="8" rx="3" fill="hsl(var(--muted) / 0.5)" stroke="none" />
        {/* Centered shield with a slash through it */}
        <g transform="translate(120, 78)">
          <path
            d="M 0 -22 L 22 -14 L 22 6 Q 22 18, 0 26 Q -22 18, -22 6 L -22 -14 Z"
            fill="hsl(var(--warning) / 0.18)"
            stroke="hsl(var(--warning))"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          {/* Diagonal "blocked" slash inside the shield */}
          <line x1="-12" y1="-12" x2="12" y2="14" stroke="hsl(var(--warning))" strokeWidth="2" strokeLinecap="round" />
        </g>
      </g>
    ),
  },
  "interrupted": {
    alt:
      "A passkey ceremony pause-icon (two vertical bars) on a soft gray " +
      "card, conveying 'the ceremony stopped before it could finish.'",
    viewBox: "0 0 240 140",
    render: () => (
      <g fill="none" stroke="hsl(var(--foreground))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Soft card surface */}
        <rect
          x="60"
          y="32"
          width="120"
          height="76"
          rx="10"
          fill="hsl(var(--muted) / 0.35)"
          stroke="hsl(var(--border))"
          strokeWidth="1.4"
        />
        {/* Centered pause glyph — two rounded bars */}
        <g transform="translate(120, 70)">
          <rect
            x="-14"
            y="-16"
            width="9"
            height="32"
            rx="3"
            fill="hsl(var(--muted-foreground))"
            stroke="none"
          />
          <rect
            x="5"
            y="-16"
            width="9"
            height="32"
            rx="3"
            fill="hsl(var(--muted-foreground))"
            stroke="none"
          />
        </g>
        {/* Subtle dashed line under the card suggesting the
            ceremony was mid-flight when it stopped. */}
        <line
          x1="76"
          y1="118"
          x2="164"
          y2="118"
          stroke="hsl(var(--muted-foreground) / 0.45)"
          strokeDasharray="3 4"
          strokeWidth="1.4"
        />
      </g>
    ),
  },
};
